import { AppWebButton as Button } from '../../../ui-foundation/adapters/web';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import type { ExtendedBenefitResult } from '@/lib/benefits-calculator';

const statusStyles = {
  'eligible': 'bg-success-light text-success border-success/20',
  'check': 'bg-mustard-light text-accent border-accent/20',
  'not-eligible': 'bg-muted text-muted-foreground border-border',
};

const statusLabels = {
  'eligible': 'Likely eligible',
  'check': 'Worth checking',
  'not-eligible': 'Unlikely',
};

const categoryIcons: Record<string, string> = {
  income: '💷',
  housing: '🏠',
  health: '🏥',
  children: '👶',
  energy: '⚡',
  local: '📍',
};

export default function BenefitCard({ benefit }: { benefit: ExtendedBenefitResult }) {
  const valueLabel = benefit.valueLabel || `~£${benefit.annualValue.toLocaleString()}/year`;

  return (
    <div className={`bg-card rounded-xl border p-4 ${benefit.urgent ? 'border-accent/40 ring-1 ring-accent/20' : 'border-border'}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          <span className="text-base">{categoryIcons[benefit.category] || '📋'}</span>
          <h3 className="text-sm font-semibold text-foreground">{benefit.name}</h3>
          {benefit.urgent && (
            <span className="flex items-center gap-0.5 text-[10px] font-semibold text-accent">
              <AlertTriangle className="h-3 w-3" />
              Time-sensitive
            </span>
          )}
        </div>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${statusStyles[benefit.status]}`}>
          {statusLabels[benefit.status]}
        </span>
      </div>

      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{benefit.description}</p>

      <div className="rounded-lg border border-border bg-background px-3 py-2 mb-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Why this showed up</p>
        <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">{benefit.reason}</p>
      </div>

      <div className="flex justify-between items-center">
        <span className="text-sm font-bold text-teal">
          {valueLabel}
        </span>
        {benefit.status !== 'not-eligible' && (
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-7 gap-1"
            asChild
          >
            <a href={benefit.applyUrl} target="_blank" rel="noopener noreferrer">
              How to apply <ExternalLink className="h-3 w-3" />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}
